import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  DocumentEditorContainerComponent,
  Toolbar,
} from '@syncfusion/ej2-react-documenteditor';
import { API } from '@/utils/API';
import { Button } from '@/components/ui/button';
import { Printer, Download, X } from 'lucide-react';

DocumentEditorContainerComponent.Inject(Toolbar);

const DocxViewer: React.FC = () => {
  const [searchParams] = useSearchParams();
  const viewUrl = searchParams.get('url') ?? '';
  const fileName = searchParams.get('filename') ?? 'Document';
  const displayId = searchParams.get('displayId') ?? '';

  const containerRef = useRef<DocumentEditorContainerComponent>(null);
  const [error, setError] = useState<string | null>(null);

  const handleCreated = () => {
    if (!containerRef.current || !viewUrl) return;

    API.authenticated
      .fetch('/document_conversions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: viewUrl }),
      })
      .then(res => {
        if (!res.ok) throw new Error(`Conversion failed (HTTP ${res.status})`);
        return res.json();
      })
      .then(data => {
        containerRef.current?.documentEditor.open(data.sfdt);
      })
      .catch((err: unknown) => {
        const message = err instanceof Error ? err.message : String(err);
        console.error('[DocxViewer] document conversion error:', message);
        setError('Failed to load document.');
      });
  };

  useEffect(() => {
    document.title = displayId || fileName;
  }, [fileName, displayId]);

  useEffect(() => {
    const w = window as unknown as {
      zE?: (widget: string, action: string) => void;
    };
    try {
      w.zE?.('webWidget', 'hide');
    } catch {
      /* widget not loaded */
    }
  }, []);

  const handlePrint = () => {
    containerRef.current?.documentEditor?.print();
  };

  const handleDownload = () => {
    if (!viewUrl) return;
    const link = document.createElement('a');
    link.href = viewUrl;
    link.download = fileName;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const isValidUrl = (() => {
    try {
      const { protocol } = new URL(viewUrl);
      return protocol === 'http:' || protocol === 'https:';
    } catch {
      return false;
    }
  })();

  if (!viewUrl || !isValidUrl) {
    return (
      <div className="flex items-center justify-center h-screen text-gray-500">
        No document URL provided.
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-screen text-red-500">
        {error}
      </div>
    );
  }

  const headerLabel = [displayId, fileName].filter(Boolean).join(' ');

  return (
    <div className="h-screen w-screen flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-white shadow-sm flex-shrink-0">
        <div className="flex items-center gap-3 min-w-0">
          <h2 className="text-lg font-semibold text-gray-900 truncate max-w-md">
            {headerLabel}
          </h2>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <Button variant="outline" size="sm" onClick={handlePrint}>
            <Printer className="h-4 w-4 mr-1" />
            Print
          </Button>
          <Button variant="outline" size="sm" onClick={handleDownload}>
            <Download className="h-4 w-4 mr-1" />
            DOCX
          </Button>
          <Button variant="ghost" size="sm" onClick={() => window.close()}>
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Syncfusion Document Editor with built-in toolbar + status bar */}
      <div className="flex-1 min-h-0">
        <DocumentEditorContainerComponent
          ref={containerRef}
          height="100%"
          enableToolbar={true}
          showPropertiesPane={false}
          restrictEditing={true}
          enableLocalPaste={false}
          created={handleCreated}
          documentEditorSettings={{ showRuler: false }}
          toolbarItems={['Find']}
        />
      </div>

      {/* Hide Syncfusion chrome that leaks through in read-only mode */}
      <style>{`
        .e-dlg-container { pointer-events: none; overflow: hidden; max-height: 0 !important; }
        .e-de-ctnr-properties-pane-btn { display: none !important; }
        .e-de-showhide-btn { display: none !important; }
      `}</style>
    </div>
  );
};

export default DocxViewer;
